import { useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Icon,
  Tag,
  Stack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Button,
  HStack,
  Link,
  Divider,
} from "@chakra-ui/react";

import { FiDownload, FiLinkedin, FiMail, FiPhone } from "react-icons/fi";
import { BiMessageAltDetail } from "react-icons/bi";

import {
  AgencyFavouriteAction
} from "./Shortlist/AgencyShortlistActions";
import {
  CLIENT_CANDIDATE_SKILLS_QUERY,
} from "./../graphql/jobs";
import colors from "./../helpers/colors";
import { CURRENT_USER_QUERY } from "./../graphql/users";
import { useQuery } from "@apollo/client";
import ProfileImage from "./common/ProfileImage";
import AvailabilityLine from "./common/AvailabilityLine";
import BullhornRecordRow from "./common/BullhornRecordRow";
import CandidateSummaryPanel from "./common/CandidateSummaryPanel";
import { bullhornSummaryFor } from "./../helpers/candidateSummary";

import ChatDrawer from './ChatDrawer/ChatDrawer';

function ViewCandidateDetails({
  candidateReport,
  showAgencyFavouriteAction,
  refetch,
}) {
  const candidate =
    candidateReport?.candidate;

  const {
    isOpen: isResumeOpen,
    onOpen: onResumeOpen,
    onClose: onResumeClose,
  } = useDisclosure();

  const [openModalIndex, setOpenModalIndex] = useState(null);

  const openModal = (index) => {
    setOpenModalIndex(index);
  };

  const closeModal = () => {
    setOpenModalIndex(null);
  };

  const { data: user } = useQuery(CURRENT_USER_QUERY);

  const { data: candidateSkillsData } = useQuery(
    CLIENT_CANDIDATE_SKILLS_QUERY,
    {
      variables: {
        candidateProfileId: candidate?.candidateProfile?.id,
      },
      skip: !candidate?.candidateProfile?.id,
    }
  );

   const candidateSkills = candidateSkillsData?.getCandidateSkillsByProfileId.flatMap(
      (role) => role.skills
    ).reduce((map, skill) => {
      if (!map.has(skill.name)) {
        map.set(skill.name, skill);
      }
      return map;
    }, new Map()).values() || [];
  const deduplicatedSkills = Array.from(candidateSkills);

  const candidateRoles = candidate?.candidateProfile?.roles ? [...candidate.candidateProfile.roles].sort((a, b) => a.name.localeCompare(b.name)) : [];

  const [detailActiveTab, setDetailActiveTab] = useState("details");

  const bullhornSummary = bullhornSummaryFor(candidate?.candidateProfile);

  const { isOpen: isChatDrawerOpen, onOpen: onChatDrawerOpen, onClose: onChatDrawerClose } = useDisclosure();

  useEffect(() => {
    const intercomWidgets = document.querySelectorAll('.intercom-launcher-frame, .intercom-launcher, .intercom-messenger-frame, .intercom-with-namespace-52k34s');

    intercomWidgets.forEach((widget) => {
      if (widget instanceof HTMLElement) {
        widget.style.right = isChatDrawerOpen ? '520px' : '20px';
      }
    });
  }, [isChatDrawerOpen]);

  const handleTabClick = (tab) => {
    setDetailActiveTab(tab);
  };

  return (
    <Flex
      direction="column"
      flex={1}
      gap="24px"
      overflowY="auto"
    >
      <Stack gap="8px">
        <Stack gap="15px" bgColor={colors.black} p="16px"
          borderRadius="12px"
          backgroundColor={colors.black}
        >
          <Flex alignItems="center">
            <ProfileImage
              imageUrl={candidate?.profileImageUrl}
              name={candidate?.fullName}
              size="65px"
            />
            <Stack ml={"24px"}>
              <Flex gap="16px" alignItems="center">
                <Text fontSize="xl" fontWeight="bold">
                  {candidate?.fullName}
                </Text>
              </Flex>
              <AvailabilityLine profile={candidate?.candidateProfile} />
            </Stack>
          </Flex>
          <HStack gap="16px">
            {showAgencyFavouriteAction && (<AgencyFavouriteAction
              candidateReport={candidateReport}
              refetch={refetch}
            />)}
            <Button
              variant="outline"
              borderColor="white"
              _hover={{ borderColor: "white", bg: "transparent" }}
              color="white"
              onClick={() => onChatDrawerOpen()}
            >
              <Icon as={BiMessageAltDetail} mr="6px" />
              Message
            </Button>
          </HStack>
        </Stack>

        <Stack gap="15px" bgColor={colors.black} p="16px"
          borderRadius="12px"
          backgroundColor={colors.black}
        >
          <HStack>
            <Button
              onClick={() => handleTabClick("details")}
              backgroundColor={detailActiveTab === "details" ? "whiteAlpha.300" : "transparent"}
              color={detailActiveTab === "details" ? "white" : "whiteAlpha.800"}
              fontWeight={detailActiveTab === "details" ? "bold" : "normal"}
              fontSize="16px"
            >
              Details
            </Button>
            <Button
              onClick={() => handleTabClick("cv")}
              backgroundColor={detailActiveTab === "cv" ? "whiteAlpha.300" : "transparent"}
              color={detailActiveTab === "cv" ? "white" : "whiteAlpha.800"}
              fontWeight={detailActiveTab === "cv" ? "bold" : "normal"}
              fontSize="16px"
            >
              CV/Resume
            </Button>
          </HStack>
          <Divider />

          {detailActiveTab === "details" && (
            <Stack gap="24px">
              <Stack gap="12px">
                <Text fontSize="xl" fontWeight="bold">
                  User Roles
                </Text>
                <Flex wrap="wrap" gap="8px">
                  {candidateRoles.map((role) => {
                    return (
                      <Tag
                        key={role.id}
                        variant="solid"
                        backgroundColor="#48BB78"
                        color="#ffffff"
                        fontSize="14px"
                        fontWeight="500"
                        padding="8px 10px"
                      >{role.name}</Tag>
                    );
                  })}
                </Flex>
              </Stack>

              <Stack gap="12px">
                <Text fontSize="xl" fontWeight="bold">
                  User Skills
                </Text>
                <Flex wrap="wrap" gap="8px">
                  {deduplicatedSkills.map((skill) => {
                    return (
                      <Tag
                        key={skill.id}
                        variant="solid"
                        backgroundColor={"#48BB78"}
                        color="#ffffff"
                        fontSize="14px"
                        fontWeight="500"
                        padding="8px 10px"
                      >
                        {skill.name}
                      </Tag>
                    );
                  })}
                </Flex>
              </Stack>

              <Stack gap="12px">
                <Text fontSize="xl" fontWeight="bold">Contact</Text>
                <Stack>
                  <HStack>
                    <Icon as={FiMail} />
                    <Link color={"white"} href={"mailto:" + candidate?.email}>{candidate?.email}</Link>
                  </HStack>
                  <HStack>
                    <Icon as={FiPhone} />
                    <Link color={"white"} href={"tel:" + candidate?.primaryPhone}>{candidate?.primaryPhone}</Link>
                  </HStack>
                  {candidate?.candidateProfile?.linkedInURL && (
                    <HStack>
                      <Icon as={FiLinkedin} />
                      <Link color={"white"} target="_blank" href={candidate?.candidateProfile?.linkedInURL}>{candidate?.candidateProfile?.linkedInURL}</Link>
                    </HStack>
                  )}
                </Stack>
              </Stack>

              <Stack gap="12px">
                <Text fontSize="xl" fontWeight="bold">
                  Referrals:&nbsp;
                  {candidate?.referrals?.length ? `(${candidate.referrals.length} referrals)` : "None yet"}
                </Text>
                <Stack>
                  <HStack mb={1}>
                    <Text>Code:</Text>
                    <Text>{candidate?.referralCode}</Text>
                  </HStack>
                </Stack>
              </Stack>

              {candidate?.candidateProfile?.agencySlugId && (
                <Flex alignItems="center" gap="4px" mt="16px">
                  <img
                    src={candidate?.candidateProfile?.agencyLogoUrl}
                    height={20}
                    alt={candidate?.candidateProfile?.agencySlugId}
                    // Height-led, so a wide wordmark keeps its shape instead of being
                    // squeezed into a 20px square.
                    style={{
                      height: "20px",
                      width: "auto",
                      maxWidth: "80px",
                      objectFit: "contain",
                      paddingRight: "4px",
                    }}
                  />
                  <Text
                    fontSize="md"
                    fontWeight="normal"
                    lineHeight="6"
                    color={colors.greyLight}
                  >
                    {candidate?.candidateProfile?.agencySlugId}
                  </Text>
                </Flex>
              )}

              <BullhornRecordRow
                bullhornId={candidate?.candidateProfile?.bullhornId}
                bullhornUrl={candidate?.candidateProfile?.bullhornUrl}
                mt="16px"
              />

            </Stack>)}
          {detailActiveTab === "cv" && (
            <Stack gap="24px">
              {/* Sits outside the resumeUrl gate below: an imported candidate
                  often has a Bullhorn blurb and no CV file at all. */}
              <CandidateSummaryPanel
                title="Bullhorn Summary"
                text={bullhornSummary}
                testId="bullhorn-summary"
              />
              {candidate?.candidateProfile?.resumeUrl && (
                <>
                  <Box>
                    <Text fontSize="xl" fontWeight="bold" mb="4">
                      CV/Resume
                    </Text>

                    {candidate?.candidateProfile?.resumeSummary ? (
                      <CandidateSummaryPanel
                        title="AI summary"
                        html={candidate?.candidateProfile?.resumeSummary}
                        testId="ai-summary"
                      />
                    ) : (
                      <>
                        <Flex
                          direction={"row"}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                          mb="4"
                        >
                          <Text fontSize="md" fontWeight="semibold" lineHeight="6">
                          </Text>

                          <a
                            href={candidate?.candidateProfile?.resumeUrl}
                            download
                            style={{ textDecoration: "none" }}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <Flex
                              alignItems="center"
                              justifyContent="center"
                              borderRadius="20px"
                              p="4px 8px"
                              fontSize="sm"
                              fontWeight="semibold"
                              lineHeight="6"
                              color="white"
                              cursor="pointer"
                            >
                              <Icon color="white" fontSize="16px" as={FiDownload} mr="2" />
                              Download
                            </Flex>
                          </a>
                        </Flex>

                        <iframe
                          title="resume"
                          width="100%"
                          height="800px"
                          src={candidate?.candidateProfile?.resumePreviewUrl}
                        ></iframe>
                      </>
                    )}
                  </Box>

                  {candidate?.candidateProfile?.resumeSummary && (
                    <Box>
                      <Button onClick={onResumeOpen} variant="outline">
                        Open CV
                      </Button>
                      <Modal
                        isOpen={isResumeOpen}
                        onClose={onResumeClose}
                        isCentered
                        size={"4xl"}
                      >
                        <ModalOverlay />
                        <ModalContent>
                          <ModalHeader color={colors.black}>
                            <Flex
                              flexDirection={"row"}
                              alignItems={"center"}
                              justifyContent={"space-between"}
                            >
                              <Text>{candidate?.fullName}</Text>
                              <a
                                href={candidate?.candidateProfile?.resumeUrl}
                                download
                                style={{
                                  textDecoration: "none",
                                  marginRight: "40px",
                                  fontSize: "14px",
                                  alignItems: "center",
                                  display: "flex",
                                  position: "relative",
                                  top: "-4px",
                                }}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <Icon
                                  color={colors.black}
                                  fontSize="16px"
                                  as={FiDownload}
                                  mr="2"
                                />
                                Download
                              </a>
                            </Flex>
                          </ModalHeader>
                          <ModalCloseButton />
                          <ModalBody>
                            <iframe
                              title="resume"
                              width="100%"
                              height="700px"
                              src={candidate?.candidateProfile?.resumePreviewUrl}
                            />
                          </ModalBody>
                          <ModalFooter>
                            <Button colorScheme="blue" mr={3} onClick={onResumeClose}>
                              Close
                            </Button>
                          </ModalFooter>
                        </ModalContent>
                      </Modal>
                    </Box>
                  )}
                </>
              )}

              {/* Certifications */}
              <Box>
                {candidate?.candidateProfile?.certification?.length > 0 && (
                  <Text fontSize="xl" fontWeight="bold" mb="2">
                    Certifications
                  </Text>
                )}
                {candidate?.candidateProfile?.certification?.map((cert, index) => (
                  <Box key={index} mt="12px">
                    <Text fontSize="sm" fontWeight="normal" mb="2">
                      {cert.name}
                    </Text>
                    <Button
                      onClick={() => openModal(index)}
                      variant="outline"
                      colorScheme=""
                    >
                      Open {cert.name}
                    </Button>
                    <Modal
                      isOpen={openModalIndex === index}
                      onClose={closeModal}
                      isCentered
                      size={"4xl"}
                    >
                      <ModalOverlay />
                      <ModalContent>
                        <ModalHeader color={colors.black}>
                          <Flex
                            flexDirection={"row"}
                            alignItems={"center"}
                            justifyContent={"space-between"}
                          >
                            <Text color={colors.black}>{cert?.name}</Text>
                            <a
                              href={cert?.url}
                              download
                              style={{
                                textDecoration: "none",
                                marginRight: "40px",
                                fontSize: "14px",
                                alignItems: "center",
                                display: "flex",
                                position: "relative",
                                top: "-4px",
                              }}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <Icon
                                color={colors.black}
                                fontSize="16px"
                                as={FiDownload}
                                mr="2"
                              />
                              Download
                            </a>
                          </Flex>
                        </ModalHeader>
                        <ModalCloseButton />
                        <ModalBody>
                          <iframe
                            title="certification"
                            width="100%"
                            height="700px"
                            src={cert?.url}
                          />
                        </ModalBody>
                        <ModalFooter>
                          <Button colorScheme="blue" mr={3} onClick={closeModal}>
                            Close
                          </Button>
                        </ModalFooter>
                      </ModalContent>
                    </Modal>
                  </Box>
                ))}
              </Box>
            </Stack>)}
        </Stack>
        <ChatDrawer
          isOpen={isChatDrawerOpen}
          onClose={onChatDrawerClose}
          currentUser={user?.currentUser}
          candidate={candidate}
        />
      </Stack>
    </Flex>
  );
}

export default ViewCandidateDetails;